import type { MeResponse } from '@licita/contracts';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  stripSearchParams,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import { createContext } from 'react';
import Shell from './components/Shell';
import { FILTROS_PADRAO, validarFiltro, type EmpresaResumo } from './lib/app';
import { validarAbaPainel } from './lib/painel';

/** Sessão + empresa ativa (a lista vem de /app/empresas; trocar limpa o cache e mantém a rota). */
export const SessionContext = createContext<{
  me: MeResponse | null;
  staging: boolean;
  signOut: () => void;
  empresas: EmpresaResumo[];
  empresaId: string | null;
  trocarEmpresa: (id: string) => void;
}>({
  me: null,
  staging: false,
  signOut: () => {},
  empresas: [],
  empresaId: null,
  trocarEmpresa: () => {},
});

const root = createRootRoute({ component: Shell });

/**
 * Quadro/lista de editais: aba, busca, filtros e ordem ficam na URL (`/?filtro=todos&uf=SP`); os padrões não aparecem.
 * É uma rota de layout sem caminho próprio: no TanStack Router a rota `/` é folha (índice) e não aceita filhos,
 * então o quadro vive aqui e tem dois filhos, o índice (`/`) e o painel do edital (`/editais/:fitId`).
 */
export const editaisRoute = createRoute({
  getParentRoute: () => root,
  id: 'quadro',
  component: lazyRouteComponent(() => import('./pages/Editais')),
  validateSearch: (raw: Record<string, unknown> & SearchSchemaInput) => validarFiltro(raw),
  search: { middlewares: [stripSearchParams(FILTROS_PADRAO)] },
});

/** Índice do quadro: só o quadro, sem painel aberto. */
export const quadroIndexRoute = createRoute({
  getParentRoute: () => editaisRoute,
  path: '/',
});

export const empresaRoute = createRoute({
  getParentRoute: () => root,
  path: '/empresa',
  component: lazyRouteComponent(() => import('./pages/Empresa')),
});

/** Contratos ganhos: lista, total vigente e cadastro. */
export const contratosRoute = createRoute({
  getParentRoute: () => root,
  path: '/contratos',
  component: lazyRouteComponent(() => import('./pages/Contratos')),
});

/**
 * Painel de um edital aprovado: dados, anexos, conversa e histórico (aba na URL, `?aba=conversa`).
 * É filho da rota do quadro: em telas largas abre como gaveta por cima do quadro (que continua montado,
 * com os filtros na mesma URL); em telas menores a página do quadro cede o lugar ao painel inteiro.
 */
export const painelRoute = createRoute({
  getParentRoute: () => editaisRoute,
  path: '/editais/$fitId',
  component: lazyRouteComponent(() => import('./pages/Painel')),
  validateSearch: (raw: Record<string, unknown> & SearchSchemaInput) => validarAbaPainel(raw),
  search: { middlewares: [stripSearchParams({ aba: 'dados' as const })] },
});

const routeTree = root.addChildren([editaisRoute.addChildren([quadroIndexRoute, painelRoute]), empresaRoute, contratosRoute]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
